import React from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAccountState } from "../hooks/useAccountState";
import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import type { FeatureKey } from "../lib/api";
import { getCharacterShortName } from "../lib/characterName";
import { canUseFeature, FEATURE_LOCK_MESSAGE } from "../lib/featureAccess";

type RequestCardProps = {
  title: string;
  description: string;
  locked?: boolean;
  onClick?: () => void;
  onLockedClick?: () => void;
};

function RequestCard({
  title,
  description,
  locked = false,
  onClick,
  onLockedClick,
}: RequestCardProps) {
  const isLocked = locked;
  return (
    <button
      type="button"
      aria-label={title}
      onClick={isLocked ? onLockedClick : onClick}
      aria-disabled={isLocked ? "true" : undefined}
      className={`h-full w-full rounded-[1.8rem] border p-5 text-left shadow-[0_12px_28px_rgba(124,90,78,0.10)] transition ${
        isLocked
          ? "border-[#E0D5CE] bg-[#F4EEE9] opacity-85"
          : "border-[#EADBC8] bg-white hover:-translate-y-0.5 hover:bg-[#FFFCFA]"
      }`}
    >
      <span
        className={`inline-flex rounded-full px-3 py-1 text-[11px] font-black ${
          isLocked ? "bg-white text-[#A1887D]" : "bg-[#FFF3E9] text-[#A46D4C]"
        }`}
      >
        {isLocked ? "ロック中" : "すぐ使える"}
      </span>
      <h3 className="mt-4 text-lg font-black text-[#5D4D46]">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-[#6B5851]">{description}</p>
      <p className="mt-4 text-sm font-bold text-[#A67C52]">
        {isLocked ? FEATURE_LOCK_MESSAGE : "開く →"}
      </p>
    </button>
  );
}

type ManagementCardProps = {
  title: string;
  description: string;
  onClick: () => void;
};

function ManagementCard({ title, description, onClick }: ManagementCardProps) {
  return (
    <button
      type="button"
      aria-label={title}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 rounded-[1.2rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3 text-left shadow-[0_8px_20px_rgba(124,90,78,0.08)] transition hover:bg-white"
    >
      <span className="min-w-0">
        <span className="block text-sm font-black text-[#5D4D46]">{title}</span>
        <span className="mt-1 block text-xs font-medium leading-5 text-[#7B6861]">{description}</span>
      </span>
      <span className="shrink-0 text-sm font-black text-[#A67C52]" aria-hidden="true">
        →
      </span>
    </button>
  );
}

export default function RequestEntry() {
  const navigate = useNavigate();
  const { accountState, loading: accountStateLoading } = useAccountState();
  const characterName = useCurrentCharacterName();
  const [lockedMessage, setLockedMessage] = React.useState<string | null>(null);
  const characterShortName = getCharacterShortName(characterName);
  const requestTitle = `${characterShortName}に頼む`;

  const isLocked = (feature: FeatureKey) =>
    accountStateLoading || !accountState || !canUseFeature(accountState, feature);
  const showLockedMessage = () => setLockedMessage(FEATURE_LOCK_MESSAGE);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/home" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">{requestTitle}</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">REQUEST HUB</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">{requestTitle}</h1>
            <p className="mt-3 text-sm leading-7 text-[#6B5851]">
              調べもの・整理・通知を、AIエージェントに任せられます。
            </p>
          </div>

          <div className="mt-8 space-y-8">
            {lockedMessage ? (
              <p role="status" className="rounded-xl bg-[#FFF8F1] px-4 py-3 text-sm font-bold text-[#A67C52]">
                {lockedMessage}
              </p>
            ) : null}

            <section aria-labelledby="request-actions-heading">
              <h2 id="request-actions-heading" className="text-base font-black text-[#5D4D46]">
                今すぐ頼む
              </h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <RequestCard
                  title="ニュース"
                  description="気になるテーマのニュースを短く整理して届けます。"
                  locked={isLocked("news")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/request/news")}
                />
                <RequestCard
                  title="企業リサーチ"
                  description="企業名やURLから公開情報を整理し、商談前の確認に使えます。"
                  locked={isLocked("company_research")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/request/company-research", { state: { startNew: true } })}
                />
                <RequestCard
                  title="レポート"
                  description="テーマを深く整理し、考える材料をまとめます。"
                  locked={isLocked("report")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/request/report")}
                />
                <RequestCard
                  title="領収書整理"
                  description="写真を送るだけで、あとから見返しやすい形に整理します。"
                  locked={isLocked("receipt_ocr")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/receipt", { state: { returnTo: "/request" } })}
                />
                <RequestCard
                  title="社長メモ"
                  description="写真を撮るだけで、文字起こし・要点整理をして保存します。"
                  locked={isLocked("memo_ocr")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/request/executive-memos")}
                />
              </div>
            </section>

            <section aria-labelledby="request-management-heading">
              <h2 id="request-management-heading" className="text-base font-black text-[#5D4D46]">
                管理・設定
              </h2>
              <div className="mt-3 grid gap-3">
                <ManagementCard
                  title="ナレッジライブラリ"
                  description="AIで使うPDFや社長メモを管理"
                  onClick={() => navigate("/request/knowledge")}
                />
                <ManagementCard
                  title="配信設定"
                  description="ニュース・レポートの定期配信を管理"
                  onClick={() => navigate("/request/schedules")}
                />
              </div>
            </section>
          </div>
        </section>
      </div>
    </main>
  );
}
